import React from 'react';
import PropTypes from 'prop-types';
import css from './CwGrid.scss';
import CwLabel from '../cwlabel/CwLabel';
import CwImage from '../cwimage/CwImage';
import CwFlexbox from '../cwflexbox/CwFlexbox';
import CwCheckBox from '../cwcheckbox/CwCheckBox';
import CwTable from '../cwtable/CwTable';
import cx from 'classnames';
import { AlignmentEnum } from '../../utils/Enums';
import { componentHasPropTypes } from '../../utils/CustomPropTypeValidation';
import IconPath from 'src/utils/IconPath';

export const CwGridSort = {
  ASCENDING: 'asc',
  DESCENDING: 'desc'
};

class CwGrid extends React.Component {
  constructor(props) {
    super(props);

    this._prevColumns = [];
    this.header = {};
    this._prevValues = [];
    this.widgetRows = [];
    this.expandedRows = [];
    this.state = {
      gridTop: 0,
      hasScrollBar: false
    };
  }

  /**
   * Checks only if the length of the array in selectedRows is the same as the allIds length
   */
  selectAllCheckbox = (allIds) => {
    return (<CwCheckBox value={this.props.selectionColumn.selectedRows ? this.props.selectionColumn.selectedRows.length === allIds.length : false}
      onChange={this.props.selectionColumn.onSelectionChange ? () => this.props.selectionColumn.onSelectionChange(this.props.selectionColumn.selectedRows.length === allIds.length ? [] : allIds) : null} />);
  };

  selectionStyle = () => {
    let result = {};
    if (this.props.selectionColumn.selectionColumnWidth) {
      result.width = this.props.selectionColumn.selectionColumnWidth;
    }
    return result;
  };

  objectsAreSame = (x, y, skipProps) => {
    var objectsAreSame = true;
    for (let propertyName in x) {
      if (skipProps.indexOf(propertyName) < 0) {
        if ((x ? !y : y) || x[propertyName] !== y[propertyName]) {
          objectsAreSame = false;
          break;
        }
      }
    }
    return objectsAreSame;
  };

  arraysEqual = (a, b, skipProps) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; ++i) {
      if (!this.objectsAreSame(a[i], b[i], skipProps)) return false;
    }
    return true;
  };

  getCellValue = (source, path) => {
    let result = source;
    for (let i = 0; i < path.length; i++) {
      if (result[path[i]] || result[path[i]] === 0 || result[path[i]] === '') {
        result = result[path[i]];
      } else {
        return undefined;
      }
    }
    return result;
  };

  getScrollBarWidth = () => {
    var inner = document.createElement('p');
    inner.style.width = "100%";
    inner.style.height = "200px";

    var outer = document.createElement('div');
    outer.style.position = "absolute";
    outer.style.top = "0px";
    outer.style.left = "0px";
    outer.style.visibility = "hidden";
    outer.style.width = "200px";
    outer.style.height = "150px";
    outer.style.overflow = "hidden";
    outer.appendChild(inner);

    document.body.appendChild(outer);
    var w1 = inner.offsetWidth;
    outer.style.overflow = 'scroll';
    var w2 = inner.offsetWidth;
    if (w1 === w2) w2 = outer.clientWidth;

    document.body.removeChild(outer);

    return (w1 - w2);
  };

  checkIfHasScrollBar = () => {
    const node = this.gridBody;
    const hasScrollBar = node.scrollHeight !== node.clientHeight;
    if (this.state.hasScrollBar !== hasScrollBar) {
      this.setState({
        hasScrollBar
      });
    }
  }

  hasFilterWidgets = (columns) => {
    if (columns.filter(column => column.filterWidget !== undefined).length > 0) {
      return true;
    }
    return false;
  }

  componentDidMount = () => {
    window.addEventListener("resize", this.checkIfHasScrollBar);

    const node = this.gridDiv;
    const rect = node.getBoundingClientRect();
    this.setState({
      gridTop: rect.top + (this.hasFilterWidgets(this.props.columns) || this.props.selectionColumn ? 64 : 32)
    });
  }

  componentDidUpdate = () => {
    this.checkIfHasScrollBar();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.checkIfHasScrollBar);
  }

  render() {
    const columns = this.props.columns.sort((a, b) => (a.order - b.order));
    const allIds = this.props.value ? this.props.value.map(item => item.id) : [];
    const content = this.props.value ? this.props.expandable ? new Array(this.props.value.length * 2) : new Array(this.props.value.length) : undefined;
    const selectedRows = (rowId) => {
      const index = this.props.selectionColumn.selectedRows.indexOf(rowId);
      let result = this.props.selectionColumn.selectedRows;
      if (index > -1) {
        result.splice(index, 1);
      } else {
        result.push(rowId);
      }
      return result;
    };

    if (!this.arraysEqual(this.props.columns, this._prevColumns, ['widget', 'filterWidget', 'readOnlyWidget'])) {
      this._prevColumns = this.props.columns;
      this.header = (
        <tr className={this.props.rowHighlighting ? css.headerRow : null}>
          {this.props.selectionColumn ?
            <th style={this.selectionStyle()}>
              {this.props.selectionColumn.selectionColumnLabel ?
                <CwLabel className={css.headerLabel} value={this.props.selectionColumn.selectionColumnLabel} />
                : this.selectAllCheckbox(allIds)
              }
            </th> : null}
          {columns.map(column => {
            return (
              <th key={column.key}
                style={column.width ? { width: column.width } : null}
                onClick={() => {
                  if (column.onColumnClick)
                    column.onColumnClick(column.key);
                }}>
                <CwFlexbox align={column.alignment}>
                  <CwLabel className={css.headerLabel} value={column.caption} />
                  {column.sorted ? <CwImage className={css.sortIcon}
                    src={column.sorted === CwGridSort.DESCENDING ? this.props.descendingIcon : this.props.ascendingIcon} /> : null}
                </CwFlexbox>
              </th>
            );
          })}
        </tr>
      );
    }

    if (this.props.value) {
      let gridRowId = 0;
      this.props.value.forEach((row, idx) => {
        gridRowId += 1;
        if (JSON.stringify(row) !== JSON.stringify(this._prevValues[idx])) {
          this.widgetRows[idx] = (<tr
            className={this.props.rowHighlighting ? css.contentRow : null}
            onClick={this.props.onRowClick ? (evt) => { return this.props.onRowClick(row, evt); } : null}
            onDoubleClick={this.props.onRowDoubleClick ? (evt) => { return this.props.onRowDoubleClick(row, evt); } : null}
            key={row.id}>
            {this.props.selectionColumn ?
              <td style={this.props.selectionColumn.selectionColumnWidth ? { width: this.props.selectionColumn.selectionColumnWidth } : null} className={css.cellCenter}>
                <CwCheckBox onChange={this.props.selectionColumn.onSelectionChange ?
                  () => this.props.selectionColumn.onSelectionChange(selectedRows(row.id))
                  : null}
                  value={this.props.selectionColumn.selectedRows ? this.props.selectionColumn.selectedRows.indexOf(row.id) >= 0 : false} />
              </td> : null}

            {columns.map((column, idx) => {
              const cellClass = cx(column.wordWrap ? css.wordWrap : {
                [css.cellLeft]: column.alignment === AlignmentEnum.LEFT,
                [css.cellCenter]: column.alignment === AlignmentEnum.CENTER,
                [css.cellRight]: column.alignment === AlignmentEnum.RIGHT,
              });

              let widgetProps = {};
              if (column.widget) {
                for (let propName in column.widget.props) {
                  if (column.widget.props.hasOwnProperty(propName) && typeof column.widget.props[propName] === "function" && propName.indexOf("on") === 0) {
                    widgetProps[propName] = (...args) => column.widget.props[propName](row, column.key, ...args);
                  }
                }
              }

              const cellValue = this.getCellValue(row, column.key);
              return (
                <td key={idx} style={column.width ? { width: column.width } : null} className={cellClass}>
                  {(column.widget && row.editContent) || (column.widget && !row.editContent && !column.readOnlyWidget) ?
                    React.cloneElement(column.widget, {
                      value: cellValue,
                      row,
                      columnKey: column.key,
                      gridRowId,
                      inAGrid: true,
                      ...column.widget.props,
                      ...widgetProps,
                    }) : !row.editContent && column.readOnlyWidget ?
                      React.cloneElement(column.readOnlyWidget, {
                        value: cellValue,
                        row,
                        columnKey: column.key,
                        gridRowId,
                        inAGrid: true,
                        ...column.readOnlyWidget.props,
                        ...widgetProps,
                      }) : Object.prototype.toString.call(cellValue) === '[object String]' ?
                        <CwLabel value={cellValue} /> :
                        cellValue}
                </td>
              );
            })}
          </tr>);
        }
        content.push(this.widgetRows[idx]);

        if (this.props.expandable && JSON.stringify(row) !== JSON.stringify(this._prevValues[idx])) {
          this.expandedRows[idx] = (
            <tr key={row.id + 'e'} style={row.expandedRow ? null : { display: 'none' }}>
              <td colSpan={this.props.selectionColumn ? columns.length + 1 : columns.length}>
                {row.expandedRow ? row.expandedRowContent : null}
              </td>
            </tr>
          );
        }
        content.push(this.expandedRows[idx]);

        this._prevValues[idx] = row;
      });
    }

    let bodyStyle = {};

    bodyStyle = this.props.scrollBodyHeight === -1 ?
      { overflow: 'auto', height: 'calc(100vh - ' + (this.state.gridTop + 48) + 'px)' } :
      { overflow: 'auto', height: this.props.scrollBodyHeight };

    return (
      <div ref={(div) => this.gridDiv = div}>
        <div style={this.state.hasScrollBar ? { paddingRight: this.getScrollBarWidth() } : {}}>
          <CwTable nested={this.props.nested} alternatingRowColors={this.props.alternatingRowColors}>
            <thead>
              {this.header}
              {this.hasFilterWidgets(columns) || this.props.selectionColumn ? 
                <tr className={css.headerRowSecond}>
                  {this.props.selectionColumn && this.props.selectionColumn.selectionColumnLabel ?
                    <th>{this.selectAllCheckbox(allIds)}</th> : this.props.selectionColumn && !this.props.selectionColumn.selectionColumnLabel ? <th /> : null}
                  {columns.map((column) => { return column.filterWidget ? <th key={column.key} >{column.filterWidget}</th> : <th key={column.key} />; })}
                </tr> 
              : null}
            </thead>
          </CwTable>
        </div>
        <div ref={(div) => this.gridBody = div} style={bodyStyle}>
          <CwTable nested={this.props.nested} alternatingRowColors={this.props.alternatingRowColors}>
            <tbody className={this.props.expandable && this.props.alternatingRowColors ? css.gridBodyExpandable : css.gridBody}>
              {content}
            </tbody>
          </CwTable>
        </div>
      </div>
    );
  }
};

CwGrid.propTypes = {
  /**
   * Array of data for the grid. Id is required for each item.
   */
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      /**
       * If the grid's prop expandable is set to true this prop can be used for showing or hiding the expanded row
       */
      expandedRow: PropTypes.bool,
      /**
       * If the grid's prop expandable is set to true this prop can be used to define the expanded row's content
       */
      expandedRowContent: PropTypes.node,
      /**
       * This prop can be used for swiching the row from readOnly to edit mode
       */
      editContent: PropTypes.bool,
    })
  ),
  /**
   * Array of objects that define the columns.
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * corresponds to the field from "value" that will be displayed in the column
       */
      key: PropTypes.arrayOf(PropTypes.string).isRequired,
      /**
       * the column's label displayed in the header
       */
      caption: PropTypes.string,
      /**
       * Define a component to be used as a template for the column cells.
       * The component's props that start with "on" and are functions will be intercepted and passed the whole row as an argument
       * The component must have 'value' propType.
       */
      widget: componentHasPropTypes(['value']),
      /**
       * Define a component to be used as a template for the column cells in readOnly mode.
       * The component must have 'value' propType.
       */
      readOnlyWidget: componentHasPropTypes(['value']),
      /**
       * option to provide fix width
       */
      width: PropTypes.number,
      /**
       * alignment of header and cell content
       */
      alignment: PropTypes.oneOf([AlignmentEnum.LEFT, AlignmentEnum.CENTER, AlignmentEnum.RIGHT]),
      /**
       * number used for ordering the columns
       */
      order: PropTypes.number,
      /**
       * if set the corresponding icon will be displayed next to the label
       */
      sorted: PropTypes.oneOf([CwGridSort.ASCENDING, CwGridSort.DESCENDING]),
      /**
       * Callback function to be called on column header click
       */
      onColumnClick: PropTypes.func,
      /**
       * Sets whether the cell is allowed to wordwrap
       */
      wordWrap: PropTypes.bool,
      /**
       * Filters the column with the value from the widget
       */
      filterWidget: PropTypes.element,
    }),
  ).isRequired,
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Path to ascending order icon
   */
  ascendingIcon: PropTypes.string,
  /**
   * Path to descending order  icon
   */
  descendingIcon: PropTypes.string,
  /**
   * Set whether to highlight row on mouse hover
   */
  rowHighlighting: PropTypes.bool,
  /**
   * Indicates that the grid should have checkboxes for each row
   */
  selectionColumn: PropTypes.shape({
    /**
     * An optional label to be shown above checkbox column
     */
    selectionColumnLabel: PropTypes.string,
    /**
     * Array of indexes coresponding to the id field of the records
     * that should have their checkboxes selected
     */
    selectedRows: PropTypes.arrayOf(PropTypes.number),
    /**
     * A callback function that will be called when a checkbox is clicked,
     * it will pass an array with the ids of all selected records
     */
    onSelectionChange: PropTypes.func,
    /**
     * option to provide fix width
     */
    selectionColumnWidth: PropTypes.number,
  }),
  /**
   * create extra rows that can be expanded
   */
  expandable: PropTypes.bool,
  /**
   * Func fired when a row is clicked
   * @param {row} row - the row clicked
   * @param {event} [evt] - the click event
   */
  onRowClick: PropTypes.func,
  /**
   * Func fired when a row is double clicked
   * @param {row} row - the row double clicked
   * @param {event} [evt] - the double click event
   */
  onRowDoubleClick: PropTypes.func,
  /**
   * Set to false if alternating row colors are not desired
   */
  alternatingRowColors: PropTypes.bool,
  /**
   * Whether the grid is a nested grid and requires different styles.
   */
  nested: PropTypes.bool,
  /**
   * Make grid body scrollable and set to a specific height (set to -1 to extend to bottom of window)
   */
  scrollBodyHeight: PropTypes.number
};

CwGrid.defaultProps = {
  rowHighlighting: true,
  alternatingRowColors: true,
  ascendingIcon: IconPath.get('Action_CollapseUp'),
  descendingIcon: IconPath.get('Action_ExpandDown'),
};

export default CwGrid;


// WEBPACK FOOTER //
// ./src/components/cwgrid/CwGrid.jsx